import React from 'react';
import { prop } from 'ramda';

import { useOnClose } from '@core/js/components/common/PopupManager';

import { NumberCard } from '@interlayer/js/app/components/main/Registration/components/common/NumberCard';
import { Input } from '@interlayer/js/components/common/Fields/Input/Input';
import Form from '@interlayer/js/components/common/Form/Form';
import { I18n } from '@interlayer/js/components/common/I18n';
import { Link } from '@interlayer/js/components/common/Link';
import { ManagedPopup } from '@interlayer/js/components/common/Popup/ManagedPopup';
import { ButtonNext } from '../../common/ButtonNext';

import { useTranslations } from '@core/js/hooks/i18n';
import { useAppSelector, useDeviceSelector } from '@core/js/store/hooks';
import buildClassName from '@core/js/utils/classNameBuilder.util';

import { useSubmitJoinNow } from '@interlayer/js/app/components/main/Registration/components/templates/OnlineReg/components/RegInSteps/components/common/steps/JoinNowStep/hooks/useSubmitJoinNow';
import { usePortraitOrientation } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { useNavigate } from '@interlayer/js/hooks/navigate';

import { TUpgradeMemberPopupProps } from './types';

import style from './UpgradeMemberPopup.scss';

export const UpgradeMemberPopup: React.FC<TUpgradeMemberPopupProps> = ({ id, membershipNumber }) => {
    const onClosePopup = useOnClose(id);
    const rootPage = useAppSelector(prop('rootPage'));
    const isMobile = useDeviceSelector(prop('isMobile'));
    const isLandscape = !usePortraitOrientation();
    const t = useTranslations('playtech.mobile.system.registration.');
    const [
        dTagRegistrationFastTrackUrl = '/fast-track',
        dTagForgottenMembershipUrl = '/forgotten-membership'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.registrationFastTrackUrl,
        DYNAMIC_TAG_NAMES.forgottenMembershipUrl
    ]);

    const navigate = useNavigate();

    const handlerUpgrade = async () => {
        onClosePopup();
        navigate(rootPage);

        await new Promise(resolve => {
            setTimeout(resolve, 200);
        });

        navigate(dTagRegistrationFastTrackUrl);
    };

    const { validationConfig, handlerSubmit } = useSubmitJoinNow({ handlerSubmitProp: handlerUpgrade });

    const renderFormMembershipNumber = () => (
        <Form
            onSubmit={handlerSubmit}
            validationConfig={validationConfig}
            render={({ handleSubmit, submitting }) => (
                <form
                    className={style.form}
                    onSubmit={handleSubmit}
                >
                    <div className={style.formWrap}>
                        <Input
                            className={buildClassName(['membership'], style)}
                            placeholder={t('joinNow.membership.label')}
                            type="text"
                            pattern="\d*"
                            inputMode="numeric"
                            isOnlyNumber
                            name="membershipNumber"
                        />

                        <Link
                            onClick={onClosePopup}
                            className={style.forgotLink}
                            href={dTagForgottenMembershipUrl}
                        >
                            <I18n
                                t={t}
                                tKey="joinNow.link.forgot"
                            />
                        </Link>
                    </div>

                    <ButtonNext
                        isLoading={submitting}
                        disabled={submitting}
                        classNames={{ wrap: style.buttonNext }}
                    />
                </form>
            )}
        />
    );

    const renderForm = () => (
        <Form
            onSubmit={() => handlerSubmit({ membershipNumber })}
            render={({ handleSubmit, submitting }) => (
                <form
                    className={style.form}
                    onSubmit={handleSubmit}
                >
                    <NumberCard
                        classNames={{ card: style.card }}
                        numberCard={membershipNumber}
                    />

                    <ButtonNext
                        isLoading={submitting}
                        disabled={submitting}
                        classNames={{ wrap: style.buttonNext }}
                    />
                </form>
            )}
        />
    );

    return (
        <ManagedPopup
            withLogo
            fullscreen
            isNotCloseAfterRedirect
            onClose={onClosePopup}
            className={clsx(style.modal, { [style.landscapeHorizontal]: isMobile && isLandscape })}
        >
            <div className={style.modalContent}>
                <I18n
                    t={t}
                    tKey="alreadyUpgradeMember.title"
                    className={style.modalTitle}
                />

                <I18n
                    t={t}
                    tKey="alreadyUpgradeMember.text"
                    className={style.modalText}
                />

                {membershipNumber
                    ? renderForm()
                    : renderFormMembershipNumber()
                }
            </div>
        </ManagedPopup>
    );
};
